/*----------------------------------
- COMPONENT: LAZY PUBLIC DOMAIN ROW DRAWER
----------------------------------*/

/**
 * @docs docs/features/product-domain-listings
 * @adr  ADR-0028
 * @rule The drawer chunk is requested only once a row is opened: it carries the eleven cards and
 *       their sheet (`domainsDrawer.css`), which a static import would put in the eager chunk of Home
 *       and `/domains*` for a drawer most visitors never open (no framer-motion since batch 7: the
 *       marketing Drawer animates in CSS). Loading, retry and the last row are the shared lazy preview
 *       drawer's (`useLazyPreviewDrawer`).
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { createPreviewDrawerLoader, useLazyPreviewDrawer } from '@/client/pages/landing/components/previewDrawer/useLazyPreviewDrawer';

// Types
import type { PublicDomainsTableRow } from '../table/PublicDomainsTable';
import type { PublicDomainRowDrawerProps } from './PublicDomainRowDrawer';

/*----------------------------------
- TYPES
----------------------------------*/

type LazyPublicDomainRowDrawerProps = Omit<PublicDomainRowDrawerProps, 'row' | 'open'> & {
    /** The row being previewed, or null while the drawer is closed. */
    row: PublicDomainsTableRow | null;
};

/*----------------------------------
- HELPERS
----------------------------------*/

const drawerLoader = createPreviewDrawerLoader<PublicDomainRowDrawerProps>(() => import('./PublicDomainRowDrawer'));

/*----------------------------------
- COMPONENT
----------------------------------*/

const LazyPublicDomainRowDrawer = ({ row, ...props }: LazyPublicDomainRowDrawerProps) => {
    const { Drawer, drawn: drawnRow, isOpen } = useLazyPreviewDrawer(drawerLoader, row, props.onRequestClose);
    if (!drawnRow || !Drawer) return null;
    return <Drawer row={drawnRow} open={isOpen} {...props} />;
};

export default LazyPublicDomainRowDrawer;
